// Ported from horizontal-actions-dashboard/frontend/src/pages/DashboardPage.tsx
// (+ features/kpi/KpiSection.tsx + features/regions/RegionsSection.tsx merged into
// one page, MUI-styled). No react-query here — data is fetched with useEffect/useState
// via horizontalActionsApi, matching this app's existing conventions.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { getSummary, getRegions, ApiError } from '../api/horizontalActionsApi';
import type { KpiSummary, RegionSummary } from '../api/horizontalActionsTypes';
import { TerminalsHeader } from './terminals-shared/TerminalsHeader';
import { useDashboardChanges } from '../hooks/useDashboardChanges';
import { TerminalsKpiCard, KpiTone } from './terminals-shared/TerminalsKpiCard';
import { TerminalsLoadingState, TerminalsErrorState } from './terminals-shared/TerminalsStateViews';
import { TerminalsProgressBar } from './terminals-shared/TerminalsProgressBar';
import { TerminalsRiskScoreCell, TerminalsRiskScoreLegend } from './terminals-shared/TerminalsRiskScore';
import { formatPercent, sortByRegionOrder } from '../utils/horizontalActionsFormat';

const RISK_BAND_TONE: Record<string, KpiTone> = {
  critical: 'risk-critical',
  high: 'risk-high',
  medium: 'risk-medium',
  low: 'risk-low',
};

function PriorityBreakdown({ high, medium, low }: { high: number; medium: number; low: number }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, fontSize: '1rem' }}>
      <Box component="span" sx={{ color: '#c0392b' }}>High - {high}</Box>
      <Box component="span" sx={{ color: '#e65100' }}>Medium - {medium}</Box>
      <Box component="span" sx={{ color: '#1e7e34' }}>Low - {low}</Box>
    </Box>
  );
}

export default function TerminalsSummary() {
  const [kpi, setKpi] = useState<KpiSummary | null>(null);
  const [regions, setRegions] = useState<RegionSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const loaded = useRef(false);

  // Only the first load shows the loading state or the full-page error. Later loads (live
  // updates, Refresh) are silent: the page stays on screen, and a failed refresh keeps the
  // figures already shown and says so.
  const loadData = useCallback(async () => {
    const firstLoad = !loaded.current;
    if (firstLoad) setLoading(true);
    try {
      const [summaryData, regionsData] = await Promise.all([getSummary(), getRegions()]);
      setKpi(summaryData);
      setRegions(regionsData);
      loaded.current = true;
      setError(null);
      setRefreshFailed(false);
    } catch (err) {
      if (firstLoad) setError(err instanceof ApiError ? err : new Error('Failed to load dashboard data.'));
      else setRefreshFailed(true);
    } finally {
      if (firstLoad) setLoading(false);
    }
  }, []);

  // Reload when the server reports a Jira or register change.
  useDashboardChanges(() => { void loadData(); });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedRegions = regions ? sortByRegionOrder(regions) : [];

  return (
    <Box sx={{ p: 3 }}>
      <TerminalsHeader title="Terminals — Summary" onRefreshed={loadData} />

      {loading && <TerminalsLoadingState label="Loading dashboard data…" />}
      {error && <TerminalsErrorState error={error} onRetry={loadData} />}
      {refreshFailed && (
        <Alert severity="warning" role="alert" sx={{ mb: 2 }} onClose={() => setRefreshFailed(false)}>
          Could not refresh. The figures shown may be out of date.
        </Alert>
      )}

      {!loading && !error && kpi && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Summary
          </Typography>
          {/* Left: the two rows of three cards. Right: the risk score, stretched over the full height. */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TerminalsKpiCard label="Total actions" value={kpi.total_actions} hint="Distinct horizontal actions" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TerminalsKpiCard
                    label="Total Actions to Complete Across Terminals"
                    value={kpi.kpi_total_to_complete}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TerminalsKpiCard
                    label="Completed"
                    value={kpi.kpi_completed}
                    tone="success"
                    hint={`${formatPercent(kpi.kpi_completed_pct)} of actions completed`}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
                Open Actions Breakdown
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TerminalsKpiCard label="Actions to Review" value={kpi.kpi_to_review} tone="danger" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TerminalsKpiCard label="In Progress" value={kpi.kpi_in_progress} tone="warning" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TerminalsKpiCard
                    label="Overdue Actions"
                    value={<PriorityBreakdown high={kpi.kpi_overdue_high} medium={kpi.kpi_overdue_medium} low={kpi.kpi_overdue_low} />}
                    tone="danger"
                    hint={
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box component="span">High: &gt;2 weeks</Box>
                        <Box component="span">Medium: &gt;6 weeks</Box>
                        <Box component="span">Low: &gt;12 weeks</Box>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3}>
              <TerminalsKpiCard
                label="Overall Risk Score"
                centered
                value={kpi.overall_risk_score ?? '—'}
                tone={kpi.overall_risk_band ? RISK_BAND_TONE[kpi.overall_risk_band] : 'default'}
                hint={
                  kpi.overall_risk_band
                    ? kpi.overall_risk_band[0].toUpperCase() + kpi.overall_risk_band.slice(1)
                    : 'Weighted roll-up across all 4 regions'
                }
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {!loading && !error && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Regional overview
          </Typography>
          {sortedRegions.length === 0 ? (
            <Typography role="status">No regions found in the parsed workbook.</Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Region</TableCell>
                      <TableCell>Completion</TableCell>
                      <TableCell>Total Actions</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Actions to Review</TableCell>
                      <TableCell>In Progress</TableCell>
                      <TableCell>Overdue Actions</TableCell>
                      <TableCell>Open High</TableCell>
                      <TableCell>Risk Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRegions.map((r) => (
                      <TableRow key={r.region_code} hover>
                        <TableCell component="th" scope="row">
                          <Link to={`/terminals/regions/${r.region_code}`}>{r.region_display_name}</Link>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <TerminalsProgressBar pct={r.kpi_completion_pct} label={`${r.region_display_name} completion`} />
                        </TableCell>
                        <TableCell>{r.kpi_total}</TableCell>
                        <TableCell>{r.kpi_completed}</TableCell>
                        <TableCell>{r.kpi_to_review}</TableCell>
                        <TableCell>{r.kpi_in_progress}</TableCell>
                        <TableCell>{r.kpi_overdue > 0 ? <strong>{r.kpi_overdue}</strong> : 0}</TableCell>
                        <TableCell>{r.kpi_open_high > 0 ? <strong>{r.kpi_open_high}</strong> : 0}</TableCell>
                        <TerminalsRiskScoreCell score={r.risk_score} band={r.risk_band} />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TerminalsRiskScoreLegend inline />
              <Typography variant="body2" sx={{ mt: 2, color: '#5a6b7c' }}>
                Completion is completed ÷ total actions to complete, summed across every terminal in the region.
                Open is in-progress actions; Overdue and Open High count in-progress actions only.
              </Typography>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
